import jwtDecode from 'jwt-decode';
import Request from '../request/request';

export default class TokenManager {
    static KEYS_TO_PERSIST = [
        'AccessToken',
        'IdToken',
        'RefreshToken',
        'ExpiresIn',
        'TokenType',
    ];

    static EXPIRED_TOKEN_ERROR_MESSAGE = 'Authentication token expired';

    constructor(AccessToken, IdToken, RefreshToken, ExpiresIn, TokenType)
    {
        if (!AccessToken || !IdToken || !RefreshToken) {
            throw new Error('Insufficient tokens provided');
        }

        this.AccessToken = AccessToken;
        this.IdToken = IdToken;
        this.RefreshToken = RefreshToken;
        this.ExpiresIn = ExpiresIn;
        this.TokenType = TokenType;
    }

    /**
     * New Up an instance of TokenManager from an object
     *
     * @param object data - likely from a network response
     *
     * @return TokenManager
     */ 
    static fromData(data)
    {
        let {AccessToken, IdToken, RefreshToken, ExpiresIn, TokenType} = data;

        let new_token_manager = new TokenManager(AccessToken, IdToken, RefreshToken, ExpiresIn, TokenType);

        return new_token_manager;
    }

    static fromResponse(data)
    {
        console.warn('fromResponse is deprecated. Use TokenManager.fromData() instead');
        return TokenManager.fromData(data);
    }

    /**
     * Fetch all the reqired tokens from local storage and new up an instance 
     * of TokenManager
     *
     * @return TokenManager
     */
    static fromLocalStorage()
    {
        let {AccessToken, IdToken, RefreshToken, ExpiresIn, TokenType} = TokenManager.retrieveTokensFromLocalStorage();

        let new_token_manager = new TokenManager(AccessToken, IdToken, RefreshToken, ExpiresIn, TokenType);

        return new_token_manager;
    }

    /**
     * Check if all tokens are avaliable in Local Storage
     *
     * @return bool
     */
    static hasTokens()
    {
        const locally_persisted_tokens = TokenManager.retrieveTokensFromLocalStorage();

        return TokenManager.KEYS_TO_PERSIST.every((item) => {
            return locally_persisted_tokens[item];
        });
    }

    /**
     * Persist tokens to local storage
     * 
     * @return void
     */
    persistToLocalStorage()
    {
        const keys_to_persist = TokenManager.KEYS_TO_PERSIST;

        for (var i = 0; i < keys_to_persist.length; i++) {
            window.localStorage.setItem(keys_to_persist[i], this[keys_to_persist[i]]);
        }
    }

    /**
     * Get tokens from local storage
     * 
     * @return object - of keys
     */
    static retrieveTokensFromLocalStorage()
    {
        const keys_to_retrieve = TokenManager.KEYS_TO_PERSIST;
        let retrieved_tokens = {} 

        for (var i = 0; i < keys_to_retrieve.length; i++) {
            retrieved_tokens[keys_to_retrieve[i]] = localStorage.getItem(keys_to_retrieve[i]);
        }

        return retrieved_tokens;
    }

    /**
     * Refresh the current tokens
     *
     * @param bool force - should the token be force refreshed
     *
     * @return void
     */
    static refreshToken(force = false)
    {
        var tokens = TokenManager.fromLocalStorage();
        var needs_refresh = false;

        try {
            needs_refresh = tokens.needsRefresh();
        } catch (err) {
            return Promise.reject(err);
        }

        if (force === true || needs_refresh) {
            return tokens.tokenRefreshRequest()
            .then((data) => {
                let new_token_manager = TokenManager.fromData(data);
                new_token_manager.persistToLocalStorage();

                return new_token_manager;
            });
        }

        return Promise.resolve();
    }

    /**
     * Does the AccessToken need to be refreshed
     *
     * If the RefreshToken is still valid then an expired 
     * AccessToken can be regenerated.
     *
     * If the RefreshToken has expired then the user should 
     * be logged out.
     *
     * AccessTokens should be refreshed half way through its life.
     *
     * @return bool
     */
    needsRefresh()
    {
        const access_token = jwtDecode(this.AccessToken);
        const halfLife = TokenManager.getHalfLife();

        const access_token_expires_in = this.tokenExpiresIn(access_token.exp);

        if (access_token_expires_in < halfLife) {
            return true;
        }

        return false;
    }

    static getHalfLife()
    {
        const validFor = localStorage.getItem('ExpiresIn') || 120;
        const halfLife = validFor / 2;

        return halfLife;
    }

    /**
     * Destory all tokens from local storage
     *
     * @return void
     */
    static destroy()
    {
        const keys_to_remove = TokenManager.KEYS_TO_PERSIST;

        for (var i = 0; i < keys_to_remove.length; i++) {
            localStorage.removeItem(keys_to_remove[i]);
        }
    }

    decodeToken()
    {
        return jwtDecode(this.IdToken);
    }

    static decode(token)
    {
        if (!token) {
            throw new Error('Token not found');
        }
        
        return jwtDecode(token);
    }

    /**
     * Seconds until expiration date
     * 
     * @param int expiration_epoch - epoch timestamp
     * 
     * @return int - number of seconds
     */
    tokenExpiresIn(expiration_epoch = 0)
    {
        return expiration_epoch - (new Date().getTime() / 1000);
    }

    tokenRefreshRequest()
    {
        const IdToken = this.decodeToken();

        return Request.post('/auth/refresh', {
            username: IdToken['cognito:username'],
        },
        {
            dont_refresh_tokens: true,
            Authorization: 'Bearer ' + this.RefreshToken,
        }
        ).catch((err) => {

        });
    }

    hasExpired(token = 'AccessToken', expKey = 'exp')
    {   
        const exp_date = this[token][expKey];
        const expires_in = this.tokenExpiresIn(exp_date);

        if (expires_in <= 0) {
            return true;
        } 

        return false;
    }
}
